import { Menu } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { buttons, colors } from '@pray/shared/components/foundations';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@pray/shared/components/ui/Accordion/Accordion';
import Button from '@pray/shared/components/ui/Button';
import Link from '@pray/shared/components/ui/Link/Link';
import { cn } from '@pray/shared/utils/styles';

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle, // Import the style function if needed for non-trigger links
} from 'components/ui/navigation-menu';
import { Sheet, SheetContent, SheetTrigger } from 'components/ui/sheet';
import useAppRoutes from 'hooks/useAppRoutes';

import { PRODUCT_LABELS, Products } from '../../types/products';
import { AI, Albums, AudioBook, CRM, CDH, Translate, Paragraph } from '../icons/features';
import PrayStudioLogo from '../icons/PrayStudioLogo';
import { FeatureTitle } from '../ui/FeatureTitle';

const EVENT_NAMES = {
  HEADER_CTA: 'Header CTA',
};

/**
 * Renders the application header, adapting layout for different screen sizes.
 * Includes navigation links, login button, and a side sheet for mobile/tablet views.
 */
export function Header() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const location = useLocation();
  const appRoutes = useAppRoutes();

  // Updated navigation items with sub-items and icons
  const navItems = [
    {
      name: 'Products',
      href: '#', // Main item usually won't navigate directly when it's an accordion trigger
      hasDropdown: true,
      subItems: [
        {
          name: PRODUCT_LABELS[Products.SOCIAL_CLIPS],
          description: 'Use AI to turn sermons into viral, shareable clips.',
          href: appRoutes.socialClips(),
          icon: <Albums />,
        },
        {
          name: PRODUCT_LABELS[Products.WORKFLOW_AI],
          description: 'Automate daily tasks to expand your ministry’s digital reach.',
          href: appRoutes.workflowAi(),
          icon: <AI />,
        },
        {
          name: PRODUCT_LABELS[Products.AI_TRANSLATIONS],
          description: 'Auto-translate into 30+ languages in your own voice.',
          href: appRoutes.translations(),
          icon: <Translate />,
        },
        {
          name: PRODUCT_LABELS[Products.MINISTRY_INSIGHTS],
          description: 'Gain insights, identify personas, unify social data.',
          href: appRoutes.ministryInsights(),
          icon: <CDH />,
        },
        {
          name: PRODUCT_LABELS[Products.CRM],
          description: 'Engage with email, messaging and announcements.',
          href: appRoutes.crm(),
          icon: <CRM />,
        },
        {
          name: PRODUCT_LABELS[Products.AI_AUDIO_BOOKS],
          description: 'Create AI audiobooks with your voice.',
          href: appRoutes.audioBooks(),
          icon: <AudioBook />,
        },
        {
          name: PRODUCT_LABELS[Products.CMS],
          description: 'Manage and syndicate sermons from Studio.',
          href: appRoutes.cms(),
          icon: <Paragraph />,
        },
      ],
    },
    // {
    //   name: 'Solutions',
    //   href: '#',
    //   hasDropdown: true,
    //   subItems: [
    //     // Mocked sub-items
    //     { name: 'Solution One', href: '/solutions/one', icon: <MockIcon name="S1" /> },
    //     { name: 'Solution Two', href: '/solutions/two', icon: <MockIcon name="S2" /> },
    //     { name: 'Solution Three', href: '/solutions/three', icon: <MockIcon name="S3" /> },
    //   ],
    // },
    { name: 'Pricing', href: appRoutes.pricing(), hasDropdown: false },
  ];

  return (
    <header className="grid w-full grid-flow-row items-center border-b border-[var(--theme-border-divider)] bg-bg-primary p-3 text-[var(--theme-text-primary)] [grid-template-areas:'left_center_right'] [grid-template-columns:minmax(138px,auto)_1fr_minmax(172px,auto)] sm:px-8 md:px-10">
      {/* Logo */}
      <div className="[grid-area:left]">
        <PrayStudioLogo />
      </div>

      {/* Desktop Navigation (md and up) */}
      <nav className="hidden items-center justify-center space-x-1 [grid-area:center] md:flex">
        {/* Reduced space-x for menu items */}
        <NavigationMenu>
          <NavigationMenuList>
            {navItems.map((item) =>
              item.hasDropdown && item.subItems ? (
                <NavigationMenuItem key={item.name}>
                  <NavigationMenuTrigger
                    triggerMode="click"
                    className="bg-transparent font-[650] uppercase text-text-secondary"
                  >
                    {item.name}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent triggerMode="click">
                    {/* Use grid layout for sub-items */}
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                      {item.subItems.map((subItem) => (
                        <li key={subItem.name}>
                          <NavigationMenuLink asChild>
                            {/* Re-use Link and FeatureTitle for consistency */}
                            <Link
                              to={subItem.href}
                              className="group flex select-none gap-4 space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                            >
                              <FeatureTitle icon={subItem.icon} />
                              <div className="flex flex-col">
                                <p className="text-base font-[550] text-text-primary">{subItem.name}</p>
                                <p className="text-sm font-[450] text-text-secondary">{subItem.description}</p>
                              </div>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              ) : (
                // Render non-dropdown items as simple links/buttons within the list
                <NavigationMenuItem key={item.name}>
                  <Link
                    to={item.href}
                    className={cn(
                      navigationMenuTriggerStyle(), // Apply similar styling
                      'font-[650] uppercase text-text-secondary'
                    )}
                  >
                    {item.name}
                  </Link>
                </NavigationMenuItem>
              )
            )}
          </NavigationMenuList>
        </NavigationMenu>
      </nav>

      {/* Menu Trigger and Login Button */}
      <div className="flex items-center justify-end [grid-area:right]">
        {/* Tablet Login Button (md and up) */}
        <div className="hidden md:block">
          <Button
            data-tapeventname={EVENT_NAMES.HEADER_CTA}
            variant={buttons.variant.primary}
            color={colors.pray_featured}
            className="w-full text-center"
            to={appRoutes.getStarted()}
          >
            Get Started
          </Button>
        </div>
        {/* Mobile/Tablet Menu Trigger (md and down) */}
        <div className="flex items-center md:hidden">
          {/* On md screens, the login button is outside the sheet trigger */}
          <Sheet>
            <SheetTrigger asChild>
              <Button
                size="icon"
                className="ml-2 text-[var(--theme-text-primary)] hover:bg-[var(--theme-bg-secondary)] focus-visible:ring-[var(--theme-border-focus)] md:ml-4"
              >
                <Menu className="size-6" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="flex w-[300px] flex-col bg-bg-primary text-text-secondary sm:w-[350px]"
            >
              {/* Close Button */}
              {/* Consider adding an explicit close button inside the sheet */}
              {/* Logo */}
              <div className="mb-6 shrink-0">
                <PrayStudioLogo />
              </div>

              {/* Navigation with Accordion */}
              <nav>
                <Accordion type="multiple" className="w-full divide-y-0">
                  {navItems.map((item) =>
                    item.hasDropdown && item.subItems ? (
                      <AccordionItem value={item.name} key={item.name}>
                        <AccordionTrigger className="py-3 text-base font-[550] text-primary hover:no-underline">
                          {item.name}
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="pb-1 pt-2">
                            <ul className="space-y-3">
                              {item.subItems.map((subItem) => (
                                <li key={subItem.name}>
                                  <Link to={subItem.href}>
                                    <FeatureTitle
                                      title={subItem.name}
                                      icon={subItem.icon}
                                      className="font-[550] text-primary"
                                    />
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ) : (
                      <Link key={item.name} to={item.href} className="block py-3 text-base font-[550] text-primary">
                        {item.name}
                      </Link>
                    )
                  )}
                </Accordion>
              </nav>

              <div className="mt-4 flex flex-col gap-4">
                <Button variant={buttons.variant.secondary} size="lg" className="hidden w-full">
                  Log In
                </Button>
                <Button
                  data-tapeventname={EVENT_NAMES.HEADER_CTA}
                  variant={buttons.variant.primary}
                  color={colors.pray_featured}
                  size="lg"
                  className="w-full text-center"
                  to={appRoutes.getStarted()}
                >
                  Get Started
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
